import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import ZoomVideo from '@zoom/videosdk';

import { usePatientLoginMutation, useVerifyUserTokenMutation } from 'services/auth/auth';
import { PatientLoginResProps, VerifyUserTokenResProps } from 'services/auth/auth.types';
import { useLazyGetMembershipPlansQuery } from 'services/lookup/lookup';
import { useLazyGetMyAccountQuery } from 'services/myAccount/myAccount';
import { useLazyGetMyProgramsQuery } from 'services/programs/programs';
import { useLazyGetSurveysQuery } from 'services/surveys/surveys';

import { selectUser } from 'store';
import { clearUser, setToken, setUser } from 'store/user/userSlice';

import Loader from 'shared/Loader';

import { useAppDispatch, useAppSelector, useQuery } from 'hooks';
import { useAnalyticsManager } from 'hooks/useAnalyticsManager/useAnalyticsManager';
import { useAppAnalytics } from 'hooks/useAppAnalytics/useAppAnalytics';
import { useDarkTheme } from 'hooks/useDarkTheme';
import { useDeepLinks } from 'hooks/useDeepLinks/useDeepLinks';
import { useMarketingParams } from 'hooks/useMarketingParams';
import { useRemoveQueryParams } from 'hooks/useRemoveQueryParams/useRemoveQueryParams';
import { useRouteMatch } from 'hooks/useRouteMatch';
import { useNotificationsCount } from 'hooks/useNotificationsCount/useNotificationsCount';
import { useSocketLifecycle } from 'hooks/useSocketLifecycle/useSocketLifecycle';
import { useUnleashContextHook } from 'hooks/useUnleashContext';
import { useWorkOSSession } from 'hooks/useWorkOSSession';
import { PathName } from 'router/paths';
import { handleRequestCatch } from 'utils/error';
import { local, LOCAL_KEYS, session, SESSION_KEYS } from 'utils/storage';
import { hasWorkOSSession } from 'utils/workos';

import { UserProgram } from './models/programs.types';
import { FeatureStatusType } from './models/user.types';
import { notifyError } from './shared/Toast/Toast';
import AppRouter from './router';

const App = () => {
  const [initialLoading, setInitialLoading] = useState(true);
  const { accessToken, userId } = useAppSelector(selectUser);
  const [patientLogin, { isLoading }] = usePatientLoginMutation();
  const [getMyAccount] = useLazyGetMyAccountQuery();
  const [getMyPrograms] = useLazyGetMyProgramsQuery();
  const [getSurveys] = useLazyGetSurveysQuery();
  const [getToken] = useVerifyUserTokenMutation();
  const [getMembershipPlans] = useLazyGetMembershipPlansQuery();
  const { loadSession } = useWorkOSSession();

  const dispatch = useAppDispatch();
  const query = useQuery();
  const navigate = useNavigate();
  const [, removeQueryParams] = useRemoveQueryParams();
  const { handleDeepLinks } = useDeepLinks();

  const isCreateNewPassword = useRouteMatch(PathName.CreateNewPassword);
  const showOnlyLoader = isLoading || initialLoading || (accessToken && !userId);

  /**
   * Evaluates pending deep links once the user's programs are loaded and replaces
   * the current history entry to avoid duplicating navigation steps.
   */
  const handleGetProgramsThen = async (data: UserProgram[], storeStatus?: FeatureStatusType) => {
    const urlToNavigate = await handleDeepLinks(data, true, storeStatus);
    if (urlToNavigate) {
      navigate(urlToNavigate, { replace: true });
    }
  };

  // use for 'sign in as patient' functionality
  const handlePatientLoginThen = async (data: PatientLoginResProps) => {
    local.set(LOCAL_KEYS.LOGOUT_EVENT, 'logout' + Math.random());
    const createAppointment = query.get('create-appointment');
    const staffId = query.get('staffId'); //
    dispatch(setUser({ ...data.data, profileImage: '' })); // * provider won't see profile image of patient
    await Promise.allSettled([
      getMyAccount().unwrap(),
      getMyPrograms().unwrap(),
      getMembershipPlans().unwrap()
    ]);
    session.set(SESSION_KEYS.VIEW_AS_PATIENT, 'true');
    staffId && session.set(SESSION_KEYS.STAFF_ID, staffId);
    navigate(
      createAppointment && JSON.parse(createAppointment)
        ? PathName.CreateAppointment
        : PathName.Home
    );
  };

  const { trackLogin } = useAnalyticsManager();

  const handleGetTokenThen = (res: VerifyUserTokenResProps) => {
    dispatch(clearUser());
    dispatch(setToken({ accessToken: res.data.accessToken, refreshToken: res.data.refreshToken }));
    // Token-based patient login (magic-link / deep-link). `handlePatientLoginThen` above is the
    // staff sign-in-as-patient flow and is intentionally NOT tracked — it's an internal tool,
    // not a patient login.
    trackLogin({ kind: 'temporary_token', method: 'token' });
  };

  const handleQueryParams = () => {
    const handleLogoutEvent = (event: StorageEvent) => {
      if (event.key == 'logout-event') {
        ZoomVideo?.destroyClient();
        dispatch(clearUser());
      }
    };
    window.addEventListener('storage', handleLogoutEvent);

    const run = async () => {
      try {
        const accessTokenFromQuery = query.get('accessToken');
        if (accessTokenFromQuery) {
          dispatch(clearUser());
          dispatch(setUser({ accessToken: accessTokenFromQuery }));
          // Auto-login from a deep link that already carries an `accessToken`. Same patient
          // login pathway as `verifyUserToken` — just without the backend round-trip — so we
          // report the same `method: 'token'`, and `kind` is what separates the two.
          trackLogin({ kind: 'access_token', method: 'token' });
        }

        const temporaryToken = query.get('token');
        if (temporaryToken) {
          if (isCreateNewPassword) {
            dispatch(clearUser());
            return;
          }
          if (accessToken) return;
          removeQueryParams('token');
          getToken({ disableActiveUsers: false, token: temporaryToken })
            .unwrap()
            .then(handleGetTokenThen)
            .catch((err) => {
              if (err.status === 401) {
                dispatch(clearUser());
              }
            });
        }

        const refetch = query.get('refetch') === 'true';
        if (refetch) {
          getMyAccount();
        }

        const loginToken = query.get('login-token');
        if (loginToken) {
          dispatch(clearUser());
          patientLogin({ loginToken, staffId: query.get('staffId') ?? '' })
            .unwrap()
            .then(handlePatientLoginThen)
            .catch(handleRequestCatch);
        }
        if (!accessToken && !accessTokenFromQuery && !query.get('token') && query.get('action')) {
          handleDeepLinks().then((urlToNavigate) => {
            if (urlToNavigate) {
              navigate(urlToNavigate);
            }
          });
        }

        // WorkOS cookie-based session: await so setInitialLoading(false) waits
        // for auth to resolve before rendering protected routes.
        if (hasWorkOSSession() && !accessToken && !accessTokenFromQuery) {
          await loadSession();
        }
      } finally {
        setInitialLoading(false);
      }
    };

    run();

    return () => window.removeEventListener('storage', handleLogoutEvent);
  };

  useDarkTheme();
  useSocketLifecycle();
  useNotificationsCount();
  useAppAnalytics();
  useMarketingParams();

  useEffect(handleQueryParams, []);

  useEffect(() => {
    const handleUpdatedAccessToken = async () => {
      const loginToken = query.get('login-token');
      if (accessToken && !loginToken) {
        query.has('action') && setInitialLoading(true);
        try {
          const programData = await getMyPrograms().unwrap();
          // A failed/aborted programs fetch (e.g. network race before the
          // mock service worker controls the page) must not take down the
          // whole bootstrap — treat missing data as an empty list.
          if ((programData ?? []).length > 0) await getSurveys().unwrap();
          const [, accountResult] = await Promise.allSettled([
            getMembershipPlans().unwrap(),
            getMyAccount().unwrap()
          ]);
          const storeStatus =
            accountResult.status === 'fulfilled'
              ? accountResult.value.data?.features?.store?.status
              : undefined;
          await handleGetProgramsThen(programData, storeStatus);
        } catch (e) {
          notifyError((e as Error).message ?? 'Error fetching data');
        } finally {
          setInitialLoading(false);
        }
      }
    };
    handleUpdatedAccessToken();
  }, [accessToken]);

  useUnleashContextHook();

  return (
    <>
      <ToastContainer className="min-w-full md:min-w-[45%]" position="top-center" />
      {showOnlyLoader ? <Loader isVisible /> : <AppRouter />}
    </>
  );
};

export default App;
